import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { QC_REJECTION_REASONS } from '@/lib/qcRejectionReasons';
import { PERMISSIONS } from '@/types/auth';
import type { QcResultInput, QcTaskDto, QcTaskUnitDto } from '@/types/quality';
import { criteriaLabel, evaluateCheckpoint } from '@/utils/qcCheckpoint';

// One row per physical barcode when a task has printed units (GrnLabelPrintTiming = "BeforeQc"),
// else one row per whole GRN line/task — see QualityControlPage's grid-shape doc comment below.
interface QcGridRow {
  id: string;
  task: QcTaskDto;
  unit: QcTaskUnitDto | null;
}

// Local draft shape while inspecting — unlike QcResultInput, passed stays null until the inspector
// actually decides it, so an untouched checkpoint is never silently sent as "Pass" (see openTask).
interface QcResultDraft {
  checkpointId: string;
  passed: boolean | null;
  measuredValue: number | null;
  remarks: string | null;
}

// One reason+qty rejection line being built in the dialog — mirrors QcRejectionInput, plus a
// client-only `id` for React keys/row identity (never sent to the server).
interface QcRejectionRow {
  id: string;
  reasonCode: string;
  qty: number;
}

export default function QualityControlPage() {
  const { t } = useTranslation('qualityControl');
  const queryClient = useQueryClient();

  const [tab, setTab] = useState(0);

  const { data: openTasks = [], isLoading: loadingOpen } = useQuery({
    queryKey: ['qc-tasks-pending'],
    queryFn: async () => (await apiClient.get<QcTaskDto[]>('/qc-tasks/pending')).data,
    enabled: tab === 0,
  });
  // Completed = finalized (Passed/Failed) tasks for the most recently received 50 GRNs — not the
  // most recent 50 tasks, since one GRN can carry several QC tasks (one per line).
  const { data: completedTasks = [], isLoading: loadingCompleted } = useQuery({
    queryKey: ['qc-tasks-completed'],
    queryFn: async () => (await apiClient.get<QcTaskDto[]>('/qc-tasks/completed', { params: { grnLimit: 50 } })).data,
    enabled: tab === 1,
  });
  const tasks = tab === 0 ? openTasks : completedTasks;

  const { data: config } = useProcessConfiguration();

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, QcResultDraft>>({});
  const [remarks, setRemarks] = useState('');
  const [rejectionRows, setRejectionRows] = useState<QcRejectionRow[]>([]);

  const selectedTask = tasks.find((t) => t.id === selectedTaskId) ?? null;
  const isFinalized = (status: string) => status === 'Passed' || status === 'Failed';
  const readOnly = selectedTask ? isFinalized(selectedTask.status) : false;
  // A finalized task reached via "View" may have been resolved through the direct Pass/Fail quick
  // action (checkpointsMandatory was false at the time) rather than actual checkpoint recording —
  // in that case every checkpoint's passed value is still null, and showing them defaulted to
  // "Pass" would misleadingly imply an inspection that never happened.
  const checkpointsWereRecorded = Boolean(selectedTask?.checkpoints.some((c) => c.passed !== null));
  // Only enforced when Barcode Required is on — with it off, GRN Label Print is disabled
  // entirely, so nothing would ever satisfy this otherwise (see QcService.SubmitAsync's matching
  // server-side gate).
  const printRequiredBeforeSubmit =
    Boolean(config?.barcodeMandatory) && config?.grnLabelPrintTiming === 'BeforeQc' && (selectedTask?.labelPrintedQty ?? 0) <= 0;
  // Only checkpoints flagged Mandatory in the QC Checkpoint Master block Submit — everything else
  // can be left undecided. See QcService.SubmitAsync's matching server-side gate.
  const missingMandatoryCheckpoints = selectedTask
    ? selectedTask.checkpoints.filter((cp) => cp.isMandatory && (results[cp.checkpointId]?.passed ?? null) === null)
    : [];

  // Quantity-wise accept/reject breakdown — replaces the old whole-task binary Pass/Fail. An empty
  // list means the full received quantity is accepted with no issues; each row rejects a specific
  // qty for its own reason. See QcService.SubmitAsync's matching server-side math/validation.
  const totalRejectedQty = rejectionRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
  const acceptedQtyPreview = selectedTask ? selectedTask.receivedQty - totalRejectedQty : 0;
  const rejectedExceedsReceived = Boolean(selectedTask) && totalRejectedQty > selectedTask!.receivedQty;
  const rejectionRowsIncomplete = rejectionRows.some((r) => !r.reasonCode || r.qty <= 0);

  const openTask = (task: QcTaskDto) => {
    setSelectedTaskId(task.id);
    setRemarks(task.remarks ?? '');
    setResults(
      Object.fromEntries(
        task.checkpoints.map((c) => [
          c.checkpointId,
          { checkpointId: c.checkpointId, passed: c.passed, measuredValue: c.measuredValue, remarks: c.remarks },
        ]),
      ),
    );
    // Empty for a still-pending task (nothing recorded yet); pre-fills the previously recorded
    // breakdown when reopened read-only via "View" on an already-finalized task.
    setRejectionRows(task.rejections.map((r) => ({ id: r.id, reasonCode: r.reasonCode, qty: r.qty })));
  };

  const addRejectionRow = () => setRejectionRows((prev) => [...prev, { id: `row-${prev.length}-${Date.now()}`, reasonCode: '', qty: 0 }]);
  const updateRejectionRow = (id: string, patch: Partial<QcRejectionRow>) =>
    setRejectionRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRejectionRow = (id: string) => setRejectionRows((prev) => prev.filter((r) => r.id !== id));

  // QcResultInput.passed is non-nullable — only checkpoints the inspector actually decided (Pass/
  // Fail toggled, or a measured value entered) are sent; untouched ones are simply omitted.
  const decidedResults = (): QcResultInput[] =>
    Object.values(results).flatMap((r) => (r.passed === null ? [] : [{ ...r, passed: r.passed }]));

  const closeDialog = () => setSelectedTaskId(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['qc-tasks-pending'] });
    queryClient.invalidateQueries({ queryKey: ['qc-tasks-completed'] });
    queryClient.invalidateQueries({ queryKey: ['putaway-tasks-pending'] });
    closeDialog();
  };

  const recordMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/qc-tasks/${selectedTaskId}/results`, { results: decidedResults(), remarks: remarks || null }),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/qc-tasks/${selectedTaskId}/results`, { results: decidedResults(), remarks: remarks || null });
      return apiClient.post(`/qc-tasks/${selectedTaskId}/submit`, {
        passed: null,
        remarks: remarks || null,
        rejections: rejectionRows.filter((r) => r.qty > 0).map((r) => ({ reasonCode: r.reasonCode, qty: r.qty, remarks: null })),
      });
    },
    onSuccess: invalidate,
  });

  const holdMutation = useMutation({
    mutationFn: () => apiClient.post(`/qc-tasks/${selectedTaskId}/hold`, { remarks: remarks || null }),
    onSuccess: invalidate,
  });

  const reworkMutation = useMutation({
    mutationFn: () => apiClient.post(`/qc-tasks/${selectedTaskId}/rework`, { remarks: remarks || null }),
    onSuccess: invalidate,
  });

  const quickHoldMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/qc-tasks/${id}/hold`, { remarks: null }),
    onSuccess: invalidate,
  });

  const quickReworkMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/qc-tasks/${id}/rework`, { remarks: null }),
    onSuccess: invalidate,
  });

  // Independent Pass/Fail per barcode (task.units non-empty) — the line only auto-finalizes once
  // every unit under it has a decision; see QcService.DecideUnitAsync.
  const decideUnitMutation = useMutation({
    mutationFn: ({ taskId, unitId, passed }: { taskId: string; unitId: string; passed: boolean }) =>
      apiClient.post(`/qc-tasks/${taskId}/units/${unitId}/decide`, { passed, remarks: null }),
    onSuccess: invalidate,
  });

  // GrnLabelPrintTiming = "BeforeQc": each task with printed labels expands into one row per
  // barcode (its own Pass/Fail). "AfterQc" (or a line whose labels aren't printed yet): one row
  // per GRN line/task, same as before.
  const gridRows: QcGridRow[] = tasks.flatMap((task): QcGridRow[] =>
    task.units.length > 0
      ? task.units.map((unit) => ({ id: unit.id, task, unit }))
      : [{ id: task.id, task, unit: null }],
  );
  const isBarcodeWise = config?.grnLabelPrintTiming === 'BeforeQc';

  const columns: DataTableColumn<QcGridRow>[] = [
    { field: 'grnNumber', headerName: t('table.grn'), render: (r) => r.task.grnNumber, searchValue: (r) => r.task.grnNumber },
    {
      field: 'materialName', headerName: t('table.material'), render: (r) => `${r.task.materialCode} — ${r.task.materialName}`,
      searchValue: (r) => `${r.task.materialCode} ${r.task.materialName}`,
    },
    { field: 'batchNo', headerName: t('table.batch'), render: (r) => r.task.batchNo ?? '—', searchValue: (r) => r.task.batchNo },
    ...(isBarcodeWise
      ? [{
          field: 'barcodeValue', headerName: t('table.barcode'), render: (r: QcGridRow) => r.unit?.barcodeValue ?? '—',
          searchValue: (r: QcGridRow) => r.unit?.barcodeValue,
        } as DataTableColumn<QcGridRow>]
      : []),
    { field: 'receivedQty', headerName: t('table.qty'), render: (r) => r.task.receivedQty, searchValue: (r) => r.task.receivedQty },
    {
      field: 'supplierName', headerName: t('table.supplier'), render: (r) => r.task.supplierName ?? '—',
      searchValue: (r) => r.task.supplierName, filterable: true,
    },
    {
      field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.unit ? r.unit.status : r.task.status} />,
      searchValue: (r) => (r.unit ? r.unit.status : r.task.status), filterable: true,
    },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.QC_EXECUTE}>
          {r.unit ? (
            r.unit.status !== 'Pending' ? null : (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small" color="success" variant="outlined" disabled={decideUnitMutation.isPending}
                  onClick={() => decideUnitMutation.mutate({ taskId: r.task.id, unitId: r.unit!.id, passed: true })}
                >
                  {t('quickActions.pass')}
                </Button>
                <Button
                  size="small" color="error" variant="outlined" disabled={decideUnitMutation.isPending}
                  onClick={() => decideUnitMutation.mutate({ taskId: r.task.id, unitId: r.unit!.id, passed: false })}
                >
                  {t('quickActions.fail')}
                </Button>
                <Button size="small" color="warning" disabled={quickHoldMutation.isPending} onClick={() => quickHoldMutation.mutate(r.task.id)}>
                  {t('quickActions.hold')}
                </Button>
                <Button size="small" color="warning" disabled={quickReworkMutation.isPending} onClick={() => quickReworkMutation.mutate(r.task.id)}>
                  {t('quickActions.rework')}
                </Button>
              </Stack>
            )
          ) : (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" variant="outlined" onClick={() => openTask(r.task)}>
                {isFinalized(r.task.status) ? t('view') : t('inspect')}
              </Button>
              {!isFinalized(r.task.status) && (
                <>
                  <Button size="small" color="warning" disabled={quickHoldMutation.isPending} onClick={() => quickHoldMutation.mutate(r.task.id)}>
                    {t('quickActions.hold')}
                  </Button>
                  <Button size="small" color="warning" disabled={quickReworkMutation.isPending} onClick={() => quickReworkMutation.mutate(r.task.id)}>
                    {t('quickActions.rework')}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('tabs.open')} />
        <Tab label={t('tabs.completed')} />
      </Tabs>

      <DataTable
        columns={columns}
        rows={gridRows}
        loading={tab === 0 ? loadingOpen : loadingCompleted}
        searchable
        getRowId={(r) => r.id}
        exportFileName={tab === 0 ? 'qc-open' : 'qc-completed'}
      />

      <Dialog open={Boolean(selectedTask)} onClose={closeDialog} maxWidth="sm" fullWidth>
        {selectedTask && (
          <>
            <DialogTitle>
              {selectedTask.materialCode} — {selectedTask.materialName}
              <Typography variant="body2" color="text.secondary">
                {t('dialog.subtitle', { grn: selectedTask.grnNumber, batch: selectedTask.batchNo ?? '—' })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {printRequiredBeforeSubmit && (
                  <Alert severity="warning">{t('dialog.printRequiredBeforeSubmit')}</Alert>
                )}
                {!readOnly && missingMandatoryCheckpoints.length > 0 && (
                  <Alert severity="warning">
                    {t('dialog.mandatoryCheckpointsRequired', { names: missingMandatoryCheckpoints.map((cp) => cp.checkpointName).join(', ') })}
                  </Alert>
                )}
                {selectedTask.checkpoints.length === 0 && (
                  <Typography color="text.secondary">{t('dialog.noCheckpoints')}</Typography>
                )}
                {readOnly && selectedTask.checkpoints.length > 0 && !checkpointsWereRecorded && (
                  <Typography color="text.secondary">{t('dialog.checkpointsNotRecorded')}</Typography>
                )}
                {(!readOnly || checkpointsWereRecorded) && selectedTask.checkpoints.map((cp) => {
                  const result = results[cp.checkpointId];
                  const isValueType = cp.valueType === 'Value';
                  const criteria = isValueType ? criteriaLabel(cp) : null;
                  return (
                    <Box key={cp.checkpointId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2">{cp.checkpointName}</Typography>
                            {cp.isMandatory && <Chip label={t('dialog.mandatory')} size="small" color="warning" />}
                          </Stack>
                          {cp.description && (
                            <Typography variant="caption" color="text.secondary">{cp.description}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t(`inspectionTypes.${cp.inspectionType}`, { ns: 'qcCheckpointMaster' })}
                            {criteria ? ` · ${t('dialog.mustBe')} ${criteria}` : ''}
                          </Typography>
                        </Box>
                        <ToggleButtonGroup
                          size="small" exclusive value={result?.passed ?? null} disabled={readOnly}
                          onChange={(_, val) => {
                            if (val === null) return;
                            setResults((prev) => ({ ...prev, [cp.checkpointId]: { ...prev[cp.checkpointId], passed: val } }));
                          }}
                        >
                          <ToggleButton value={true} color="success">
                            <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> {t('dialog.pass')}
                          </ToggleButton>
                          <ToggleButton value={false} color="error">
                            <CancelIcon fontSize="small" sx={{ mr: 0.5 }} /> {t('dialog.fail')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                      {isValueType && (
                        <TextField
                          type="number" label={t('dialog.actualValue')} size="small" sx={{ mt: 1, minWidth: 160 }}
                          value={result?.measuredValue ?? ''}
                          disabled={readOnly}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const value = raw === '' ? null : Number(raw);
                            setResults((prev) => ({
                              ...prev,
                              [cp.checkpointId]: {
                                ...prev[cp.checkpointId],
                                measuredValue: value,
                                passed: value === null ? null : evaluateCheckpoint(value, cp),
                              },
                            }));
                          }}
                        />
                      )}
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  );
                })}

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">{t('dialog.rejections')}</Typography>
                    {!readOnly && (
                      <Button size="small" startIcon={<AddIcon />} onClick={addRejectionRow}>
                        {t('dialog.addRejection')}
                      </Button>
                    )}
                  </Stack>

                  {rejectionRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('dialog.noRejections')}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {rejectionRows.map((row) => (
                        <Stack key={row.id} direction="row" spacing={1} alignItems="center">
                          <SearchableSelect
                            label={t('dialog.reason')}
                            value={row.reasonCode}
                            onChange={(value) => updateRejectionRow(row.id, { reasonCode: value })}
                            fullWidth
                            options={QC_REJECTION_REASONS}
                            disabled={readOnly}
                          />
                          <TextField
                            type="number" label={t('dialog.rejectQty')} size="small" sx={{ width: 110 }}
                            value={row.qty} disabled={readOnly}
                            onChange={(e) => updateRejectionRow(row.id, { qty: Number(e.target.value) })}
                          />
                          {!readOnly && (
                            <IconButton size="small" onClick={() => removeRejectionRow(row.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  <Typography variant="caption" color={rejectedExceedsReceived ? 'error' : 'text.secondary'} display="block" sx={{ mt: 1 }}>
                    {t('dialog.qtySummary', {
                      received: selectedTask.receivedQty, rejected: totalRejectedQty, accepted: Math.max(0, acceptedQtyPreview),
                    })}
                    {rejectedExceedsReceived && ` — ${t('dialog.rejectedExceedsReceived')}`}
                  </Typography>
                </Box>

                <TextField
                  label={t('dialog.remarks')} multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth
                  disabled={readOnly}
                />

                {selectedTask.status !== 'PendingQc' && <Chip label={selectedTask.status} size="small" sx={{ alignSelf: 'flex-start' }} />}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Button onClick={closeDialog}>{t('cancel')}</Button>
              {readOnly ? null : (
                <>
                  <Button color="warning" onClick={() => holdMutation.mutate()} disabled={holdMutation.isPending}>
                    {t('dialog.hold')}
                  </Button>
                  <Button color="warning" onClick={() => reworkMutation.mutate()} disabled={reworkMutation.isPending}>
                    {t('dialog.rework')}
                  </Button>
                  <Button variant="outlined" onClick={() => recordMutation.mutate()} disabled={recordMutation.isPending}>
                    {t('dialog.saveResults')}
                  </Button>
                  <Button
                    variant="contained" onClick={() => submitMutation.mutate()}
                    disabled={
                      submitMutation.isPending || printRequiredBeforeSubmit || missingMandatoryCheckpoints.length > 0 ||
                      rejectionRowsIncomplete || rejectedExceedsReceived
                    }
                  >
                    {t('dialog.submit')}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
